import type { JSX, ReactNode } from "react";
import { Divider, Flex, Heading, View } from "@aws-amplify/ui-react";

const HEADER_HEIGHT = "3.5rem";
const TOOLBAR_HEIGHT = "3rem";
const SEARCH_WIDTH = "18rem";

type ListingLayoutProperties = {
  /** Page title, or the folder trail leading to it. */
  readonly title: ReactNode;
  /** Omitted by views with nothing to search, such as an upload in progress. */
  readonly search?: ReactNode;
  /** Pagination, refresh, then one button per available action, in that order. */
  readonly controls?: ReactNode;
  /** The bordered panel holding the table. */
  readonly children: ReactNode;
  readonly message: ReactNode;
  /** Buttons that close out a task, shown under the panel. */
  readonly footer?: ReactNode;
};

/**
 * The shared frame every listing renders inside the content card.
 */
export function ListingLayout({
  title,
  search,
  controls,
  children,
  message,
  footer,
}: ListingLayoutProperties): JSX.Element {
  const hasToolbar = search !== undefined || controls !== undefined;

  const toolbarRow = (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      minHeight={TOOLBAR_HEIGHT}
    >
      <View flex="1" maxWidth={SEARCH_WIDTH}>
        {search}
      </View>
      <Flex alignItems="center" gap="0.5rem">
        {controls}
      </Flex>
    </Flex>
  );

  const footerRow = <Flex justifyContent="flex-end">{footer}</Flex>;

  return (
    <Flex direction="column" gap="0" height="100%">
      <Flex
        alignItems="center"
        minHeight={HEADER_HEIGHT}
        paddingInline="1.5rem"
      >
        <Heading level={2}>{title}</Heading>
      </Flex>
      <Divider />
      <Flex
        direction="column"
        flex="1"
        gap="1rem"
        minHeight="0"
        padding="1.5rem"
      >
        {hasToolbar ? toolbarRow : null}
        {children}
        <View>{message}</View>
        {footer ? footerRow : null}
      </Flex>
    </Flex>
  );
}
